import { catImage, CAT_IMG } from "@/lib/catImage";
import { useState as _useStateHV, useEffect as _useEffectHV } from "react";

import { useEffect, useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Search, Sparkles, MapPin, Heart, ShoppingCart, ShieldCheck, Lock, RotateCcw,
  ArrowRight, Store, Package, Star, ChevronRight, ChevronLeft, ChevronDown, Headphones, Gift, Smartphone, Image as ImageIcon,
} from "lucide-react";
import api from "@/lib/api";
import { resolveRoute } from "@/lib/aiSearch";
import SmartSuggest from "@/components/SmartSuggest";
import { TopDeals, CategoryRows } from "@/components/FeedProducts";
import { Logo, LogoMark } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import AccountMenu from "@/components/AccountMenu";
import { TRAVEL_CATEGORIES, mobilityPriceLine, mobilityIsBookable } from "@/lib/travelTypes";

import { FareEstimator } from "@/components/FareEstimator";

function NearbyRides() {
  const [items, setItems] = useState([]);
  const [loc, setLoc] = useState(null);
  useEffect(() => { api.get("/marketplace/location").then((r) => setLoc(r.data.location || null)).catch(() => {}); }, []);
  useEffect(() => {
    const geo = {};
    if (loc?.state) geo.state = loc.state; if (loc?.district) geo.district = loc.district; if (loc?.pincode) geo.pincode = loc.pincode;
    Promise.all(["cab", "auto", "movers", "mini_truck", "goods_transport"].map((k) => {
      const qs = new URLSearchParams({ travel_type: k, ...geo });
      return api.get(`/marketplace/public/services?${qs.toString()}`).then((r) => r.data.services || []).catch(() => []);
    })).then((lists) => {
      const seen = new Set(); const merged = [];
      for (const arr of lists) for (const s of arr) { if (!seen.has(s.service_id)) { seen.add(s.service_id); merged.push(s); } }
      merged.sort((a, b) => (a.serves_your_area === b.serves_your_area ? 0 : a.serves_your_area ? -1 : 1));
      setItems(merged.slice(0, 8));
    });
  }, [loc]);
  if (!items.length) return null;
  const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
  const imgSrc = (u) => (u && u.startsWith("/api") ? BACKEND + u : u);
  return (
    <section className="py-4" data-testid="home-nearby-rides">
      <div className="mb-2 flex items-center gap-2">
        <h2 className="text-lg font-bold text-slate-900">Cabs &amp; movers near you</h2>
        {loc?.state && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700"><MapPin className="h-3 w-3" /> {loc.district || loc.state}</span>}
      </div>
      <div className="flex gap-3 overflow-x-auto pb-1">
        {items.map((s) => (
          <Link key={s.service_id} to={`/service/${s.slug}`} data-testid={`nearby-ride-${s.service_id}`} className="w-56 shrink-0 rounded-2xl border border-slate-200 bg-white p-3 transition hover:-translate-y-0.5 hover:shadow-md">
            {s.images?.[0] ? <img src={imgSrc(s.images[0])} alt={s.name} className="mb-2 h-28 w-full rounded-xl object-cover" /> : <div className="mb-2 flex h-28 w-full items-center justify-center rounded-xl bg-gradient-to-br from-sky-100 to-fuchsia-100 text-sky-500"><Sparkles className="h-6 w-6" /></div>}
            <p className="line-clamp-1 text-sm font-semibold text-slate-800">{s.name}</p>
            <p className="mt-0.5 line-clamp-1 text-[11px] text-slate-500">{mobilityPriceLine(s.mobility || {})}</p>
            {["per_km", "per_km_hour"].includes(s.mobility?.pricing_model) && Number(s.mobility?.rate_km) > 0 && <FareEstimator mob={s.mobility} compact />}
            <span className={`mt-1 inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold ${mobilityIsBookable(s.mobility || {}) ? "bg-emerald-50 text-emerald-700" : "bg-violet-50 text-violet-700"}`}>{mobilityIsBookable(s.mobility || {}) ? "Book Now" : "Request Quote"}</span>
          </Link>
        ))}
      </div>
    </section>
  );
}

function HomeTravelServices({ nav }) {
  const [svcCats, setSvcCats] = useState([]);
  useEffect(() => { api.get("/marketplace/service-categories").then((r) => setSvcCats(r.data.categories || [])).catch(() => {}); }, []);
  return (
    <section className="py-8" data-testid="home-travel-services">
      <div className="rounded-3xl border border-slate-200 bg-gradient-to-br from-sky-50 via-white to-fuchsia-50 p-6 sm:p-8">
        <div className="mb-1 inline-flex items-center gap-2 rounded-full bg-white/70 px-3 py-1 text-[11px] font-semibold text-sky-700 ring-1 ring-sky-100"><Sparkles className="h-3.5 w-3.5" /> Travel & Services</div>
        <h2 className="text-2xl font-bold text-slate-900">Book travel or find a service</h2>
        <p className="mt-1 text-sm text-slate-500">Pick a category to book a posted fare or ask for a custom rate — or browse services by category.</p>
        <div className="mt-4 -mx-1 flex gap-2 overflow-x-auto px-1 pb-1 [scrollbar-width:thin]" data-testid="home-travel-tabs">
          {TRAVEL_CATEGORIES.map((c) => { const Icon = c.icon; return (
            <button key={c.key} onClick={() => nav(`/services?travel_cat=${c.key}`)} data-testid={`home-travel-${c.key}`}
              className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-slate-200 bg-white px-3.5 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-sky-300 hover:bg-sky-50 hover:text-sky-700">
              <Icon className="h-3.5 w-3.5" /> {c.label}
            </button>
          ); })}
        </div>
        <NearbyRides />
        <div className="mt-4 max-w-sm">
          <label htmlFor="home-svc-cat" className="text-xs font-semibold text-slate-500">Browse services by category</label>
          <div className="relative mt-1">
            <select id="home-svc-cat" data-testid="home-service-category" defaultValue=""
              onChange={(e) => { const id = e.target.value; if (id) nav(`/services?category=${id}`); }}
              className="h-11 w-full appearance-none rounded-xl border border-slate-200 bg-white px-3 pr-10 text-sm font-medium text-slate-700 outline-none focus:border-fuchsia-400">
              <option value="" disabled>Choose a service category…</option>
              {svcCats.map((c) => <option key={c.service_category_id} value={c.service_category_id}>{c.name}</option>)}
            </select>
            <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          </div>
        </div>
      </div>
    </section>
  );
}

const CHIPS = [
  { icon: Headphones, label: "Find headphones under ₹5,000", q: "headphones under 5000" },
  { icon: Gift, label: "Gift for my mother", q: "gift for my mother" },
  { icon: Smartphone, label: "Compare smartphones", q: "compare smartphones" },
  { icon: MapPin, label: "Find products near me", q: "products near me" },
];
const AI_PROMPTS = [
  "Find a birthday gift under ₹2,000",
  "Compare the best laptops",
  "Find organic products near me",
  "Show me today's deals",
];
const TRUST = [
  { icon: ShieldCheck, t: "Verified Sellers" },
  { icon: Lock, t: "Secure Payments" },
  { icon: RotateCcw, t: "Easy Returns" },
  { icon: Sparkles, t: "AI-Powered Search" },
];
const disc = (p) => (p.mrp && p.price && p.mrp > p.price ? Math.round(((p.mrp - p.price) / p.mrp) * 100) : 0);
const HERO_IMG = "https://images.unsplash.com/photo-1700190827565-7b6247de4838?q=80&w=1100&auto=format&fit=crop";
const RAIL_TESTID = { deals: "rail-deals", trending: "rail-trending", new: "rail-new", near_you: "rail-near-you", spotlight: "rail-spotlight" };
const trackHome = (rail, action, product_id) => api.post("/marketplace/reco/event", { rail, action, product_id }).catch(() => {});

export default function Landing() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [cats, setCats] = useState([]);
  const [products, setProducts] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [cms, setCms] = useState(null);
  const [rails, setRails] = useState({});

  useEffect(() => {
    api.get("/marketplace/public/categories-tree").then((r) => setCats(r.data.categories || [])).catch(() => {});
    api.get("/marketplace/public/products").then((r) => setProducts(r.data.products || [])).catch(() => {});
    api.get("/marketplace/public/sellers").then((r) => setSellers(r.data.sellers || [])).catch(() => {});
    api.get("/marketplace/public/home").then((r) => { setCms(r.data.cms); setRails(r.data.rails || {}); }).catch(() => {});
  }, []);

  // SEO — Super Admin controlled meta (title/description/OG).
  useEffect(() => {
    const seo = cms?.seo; if (!seo) return;
    const setMeta = (sel, attr, val) => {
      if (!val) return;
      let el = document.head.querySelector(sel);
      if (!el) { el = document.createElement("meta"); const [a, v] = sel.replace(/meta\[|\]/g, "").split("="); el.setAttribute(a, v.replace(/["']/g, "")); document.head.appendChild(el); }
      el.setAttribute(attr, val);
    };
    if (seo.meta_title) document.title = seo.meta_title;
    setMeta('meta[name="description"]', "content", seo.meta_description);
    setMeta('meta[property="og:title"]', "content", seo.meta_title);
    setMeta('meta[property="og:description"]', "content", seo.meta_description);
    setMeta('meta[property="og:image"]', "content", seo.og_image);
    let can = document.head.querySelector('link[rel="canonical"]');
    if (!can) { can = document.createElement("link"); can.setAttribute("rel", "canonical"); document.head.appendChild(can); }
    can.setAttribute("href", "https://www.suffleonline.com/");
    let ld = document.getElementById("suffle-jsonld");
    if (!ld) { ld = document.createElement("script"); ld.type = "application/ld+json"; ld.id = "suffle-jsonld"; document.head.appendChild(ld); }
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org", "@type": "WebSite",
      name: seo.meta_title || "Suffle Online", url: "https://www.suffleonline.com/",
      description: seo.meta_description || "Shop. Discover. Suffle.",
      potentialAction: { "@type": "SearchAction", target: "https://www.suffleonline.com/shop?q={search_term_string}", "query-input": "required name=search_term_string" },
      publisher: { "@type": "Organization", name: "Suffle Online", url: "https://www.suffleonline.com/", ...(seo.og_image ? { logo: seo.og_image } : {}) },
    });
  }, [cms?.seo]);

  const goShop = (query) => nav(`/shop${query ? `?q=${encodeURIComponent(query)}` : ""}`);
  const askAI = async () => { trackHome("hero_cta", "click"); const t = (q || "").trim(); if (!t) { goShop(""); return; } const { route } = await resolveRoute(t); nav(route); };
  const runQuery = async (t) => { const s = (t || "").trim(); if (!s) { goShop(""); return; } trackHome("hero_cta", "click"); const { route } = await resolveRoute(s); nav(route); };

  const hero = cms?.hero || { badge: "AI-Powered Marketplace", title_prefix: "Shop Smarter", title_highlight: "with Suffle AI", subtitle: "Find the right products, compare options and shop from verified sellers — simply ask Suffle.", cta_label: "Ask Suffle AI" };
  const heroSlides = cms?.hero_slides || [];
  const promoBanners = cms?.promo_banners || [];
  const banner = cms?.banner;
  const sections = (cms?.sections || []).filter((s) => s.enabled);

  const renderRail = (id, sec) => {
    const items = rails[id] || [];
    return <Rail key={id} id={RAIL_TESTID[id]} rail={id} title={sec.title} subtitle={sec.subtitle} items={items} nav={nav} onView={() => goShop(id === "deals" ? "deals" : "")} />;
  };

  useEffect(() => { if (banner?.enabled) trackHome("banner", "impression"); }, [banner?.enabled]);

  const renderSection = (sec) => {
    switch (sec.id) {
      case "categories":
        return (
          <section key="categories" id="categories" className="py-10" data-testid="section-categories">
            <div className="mb-5 flex items-end justify-between"><h2 className="text-2xl font-bold">{sec.title}</h2><button onClick={() => goShop("")} className="inline-flex items-center gap-1 text-sm font-semibold text-blue-600">View All Categories <ChevronRight className="h-4 w-4" /></button></div>
            {cats.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500" data-testid="categories-empty">Categories will appear here as the marketplace grows.</div>
            ) : (
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
                {cats.slice(0, 12).map((c) => (
                  <button key={c.category_id} onClick={() => goShop(c.name)} data-testid={`cat-${c.category_id}`} className="group relative aspect-[4/3] overflow-hidden rounded-2xl ring-1 ring-slate-200 transition hover:shadow-md">
                    <img src={c.image || catImage(c.name)} alt={c.name} loading="lazy" className="h-full w-full object-cover transition duration-500 group-hover:scale-110" />
                    <span className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/15 to-transparent" />
                    <span className="absolute inset-x-0 bottom-0 p-2.5 text-left text-xs font-semibold text-white sm:text-sm">{c.name}</span>
                  </button>
                ))}
              </div>
            )}
          </section>
        );
      case "featured":
        return (
          <section key="featured" className="py-10" data-testid="section-featured">
            <div className="mb-1 flex items-end justify-between"><h2 className="text-2xl font-bold">{sec.title}</h2><button onClick={() => goShop("")} className="inline-flex items-center gap-1 text-sm font-semibold text-blue-600">View All Products <ChevronRight className="h-4 w-4" /></button></div>
            <p className="mb-5 text-sm text-slate-500">{sec.subtitle}</p>
            {products.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 p-10 text-center" data-testid="featured-empty">
                <Store className="mx-auto h-8 w-8 text-slate-300" />
                <p className="mt-3 font-semibold text-slate-700">Products are on their way</p>
                <p className="mt-1 text-sm text-slate-500">Verified sellers are stocking up. Check back soon — no placeholder products are shown here.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                {products.slice(0, 12).map((p, idx) => (
                  <div key={`${p.product_id}-${idx}`} onClick={() => nav(`/product/${p.slug || p.product_id}`)} data-testid={`prod-${p.product_id}`} className="cursor-pointer overflow-hidden rounded-2xl border border-slate-200 bg-white transition hover:shadow-md">
                    <div className="aspect-square bg-slate-100">{(p.thumb || (p.images || [])[0]) ? <img src={p.thumb || p.images[0]} alt={p.name} loading="lazy" className="h-full w-full object-contain" /> : <div className="flex h-full items-center justify-center"><Package className="h-8 w-8 text-slate-300" /></div>}</div>
                    <div className="p-3">
                      {p.brand && <p className="truncate text-[10px] uppercase text-slate-400">{p.brand}</p>}
                      <p className="line-clamp-2 text-sm font-medium">{p.name}</p>
                      <div className="mt-1 flex items-baseline gap-1"><span className="font-bold">₹{Math.round(p.price || 0)}</span>{disc(p) > 0 && <><span className="text-xs text-slate-400 line-through">₹{Math.round(p.mrp)}</span><span className="text-xs font-semibold text-emerald-600">{disc(p)}% OFF</span></>}</div>
                      {p.rating && <p className="mt-1 flex items-center gap-1 text-xs text-amber-500"><Star className="h-3 w-3 fill-amber-400" /> {p.rating}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        );
      case "spotlight":
        return (
          <div key="spotlight-wrap">
            {cms?.spotlight_image && <img src={cms.spotlight_image} alt={sec.title} className="mb-4 w-full rounded-3xl object-cover ring-1 ring-slate-200" data-testid="spotlight-poster" />}
            {renderRail(sec.id, sec)}
          </div>
        );
      case "deals": case "trending": case "new": case "near_you":
        return renderRail(sec.id, sec);
      case "promo_banners":
        if (promoBanners.length === 0) return null;
        return (
          <section key="promo_banners" className="py-10" data-testid="section-promo-banners">
            {sec.title && <h2 className="mb-5 text-2xl font-bold">{sec.title}</h2>}
            <div className="grid gap-4 sm:grid-cols-2">
              {promoBanners.map((b, i) => {
                const go = () => { if (!b.link) return; b.link.startsWith("http") ? window.open(b.link, "_blank") : nav(b.link); };
                const square = b.placement === "category" || b.placement === "small_promo";
                return (
                  <button key={i} onClick={go} data-testid={`promo-banner-${i}`} className={`group relative overflow-hidden rounded-2xl ring-1 ring-slate-200 transition hover:shadow-md ${square ? "aspect-[4/3]" : "aspect-[16/6]"} ${b.placement === "collection" ? "sm:col-span-2 sm:aspect-[21/9]" : ""}`}>
                    {(b.image || b.mobile_image) ? <img src={b.image || b.mobile_image} alt={b.title || "Promotion"} loading="lazy" className="h-full w-full object-cover transition duration-500 group-hover:scale-105" /> : <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-blue-50 to-fuchsia-50 text-slate-300"><ImageIcon className="h-8 w-8" /></div>}
                    {(b.title || b.subtitle || b.cta_label) && (
                      <span className="absolute inset-0 flex flex-col justify-center gap-1 bg-gradient-to-r from-black/60 via-black/20 to-transparent p-5 text-left sm:p-7">
                        {b.title && <span className="max-w-xs text-lg font-bold text-white drop-shadow sm:text-2xl">{b.title}</span>}
                        {b.subtitle && <span className="max-w-xs text-xs text-white/90 sm:text-sm">{b.subtitle}</span>}
                        {b.cta_label && <span className="mt-1 inline-flex w-fit items-center gap-1 rounded-full bg-white px-4 py-1.5 text-xs font-semibold text-slate-900">{b.cta_label} <ArrowRight className="h-3.5 w-3.5" /></span>}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </section>
        );
      case "ai":
        return (
          <section key="ai" className="mb-10 rounded-3xl bg-gradient-to-br from-blue-50 via-violet-50 to-fuchsia-50 p-6 sm:p-8" data-testid="section-ai">
            <div className="flex items-center gap-3"><span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-600 to-fuchsia-600 text-white"><Sparkles className="h-5 w-5" /></span><div><h2 className="text-xl font-bold">{sec.title}</h2><p className="text-sm text-slate-500">Find, compare and buy — all with AI.</p></div></div>
            <div className="mt-5 grid gap-2 sm:grid-cols-2">
              {AI_PROMPTS.map((p) => (
                <button key={p} onClick={() => goShop(p)} data-testid={`ai-${p.slice(0, 6)}`} className="flex items-center justify-between rounded-xl bg-white px-4 py-3 text-left text-sm font-medium text-slate-700 shadow-sm transition hover:shadow-md"><span>{p}</span><ArrowRight className="h-4 w-4 text-blue-600" /></button>
              ))}
            </div>
          </section>
        );
      case "sellers": {
        const feat = cms?.featured_sellers || [];
        const ordered = feat.length ? [...sellers].sort((a, b) => { const ia = feat.indexOf(a.slug), ib = feat.indexOf(b.slug); return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib); }) : sellers;
        return (
          <section key="sellers" id="sellers" className="py-10" data-testid="section-sellers">
            <h2 className="mb-1 text-2xl font-bold">{sec.title}</h2>
            <p className="mb-5 text-sm text-slate-500">{sec.subtitle}</p>
            {ordered.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500" data-testid="sellers-empty">Verified sellers will be featured here as they join Suffle.</div>
            ) : (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                {ordered.slice(0, 10).map((s) => (
                  <Link key={s.slug} to={`/${s.slug}`} data-testid={`seller-${s.slug}`} className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-3 transition hover:shadow-md">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-slate-100">{s.logo ? <img src={s.logo} alt="" className="h-full w-full object-cover" /> : <Store className="h-5 w-5 text-slate-400" />}</span>
                    <span className="min-w-0"><span className="flex items-center gap-1 truncate text-sm font-semibold">{s.name}{s.verified && <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" />}</span>{s.location && <span className="truncate text-xs text-slate-400">{s.location}</span>}</span>
                  </Link>
                ))}
              </div>
            )}
          </section>
        );
      }
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="marketplace-home">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/95 backdrop-blur" data-testid="market-header">
        <div className="mx-auto flex max-w-[1500px] flex-wrap items-center gap-3 px-4 py-3 lg:px-8">
          <Link to="/" className="shrink-0"><Logo variant="sm" /></Link>
          <div className="order-3 flex min-w-full flex-1 items-center lg:order-2 lg:min-w-0">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-3 top-3 z-10 h-4 w-4 text-slate-400" />
              <SmartSuggest scope="all" value={q} onChange={setQ} onSubmit={(v) => runQuery(v)}
                onPick={(it) => { if (it.type === "service" && it.slug) nav(`/service/${it.slug}`); else if (it.type === "product" && it.id) nav(`/product/${it.id}`); else runQuery(it.label); }}
                placeholder="Search products, services, logistics, pages… or ask Suffle AI" testid="header-search" className="relative w-full"
                inputClassName="w-full rounded-l-full border border-r-0 border-slate-200 py-2.5 pl-9 pr-3 text-sm outline-none focus:border-blue-400" />
            </div>
            <button onClick={askAI} data-testid="header-ask-ai" className="inline-flex shrink-0 items-center gap-1.5 rounded-r-full bg-gradient-to-r from-blue-600 via-violet-600 to-fuchsia-600 px-4 py-2.5 text-sm font-semibold text-white">
              Ask Suffle AI <ArrowRight className="h-4 w-4" />
            </button>
          </div>
          <div className="order-2 ml-auto flex items-center gap-4 lg:order-3">
            <span className="hidden items-center gap-1 text-xs text-slate-500 sm:flex"><MapPin className="h-4 w-4 text-blue-600" /><span>Deliver to<br /><b className="text-slate-700">Your location</b></span></span>
            <button onClick={() => goShop("")} className="flex flex-col items-center text-slate-600 hover:text-blue-600" data-testid="header-wishlist"><Heart className="h-5 w-5" /><span className="text-[10px]">Wishlist</span></button>
            <button onClick={() => goShop("")} className="flex flex-col items-center text-slate-600 hover:text-blue-600" data-testid="header-cart"><ShoppingCart className="h-5 w-5" /><span className="text-[10px]">Cart</span></button>
            {user ? (
              <AccountMenu variant="light" />
            ) : (
              <>
                <Link to="/login" className="rounded-full border border-slate-300 px-4 py-1.5 text-sm font-semibold text-slate-700 hover:border-blue-400" data-testid="header-login">Login</Link>
                <Link to="/register" className="rounded-full bg-gradient-to-r from-blue-600 to-fuchsia-600 px-4 py-1.5 text-sm font-semibold text-white" data-testid="header-signup">Sign Up</Link>
              </>
            )}
          </div>
          <nav className="order-4 flex w-full gap-6 text-sm font-semibold text-slate-700 lg:w-auto">
            <button onClick={() => goShop("")} className="hover:text-blue-600" data-testid="nav-shop">Shop</button>
            <Link to="/services" className="hover:text-blue-600" data-testid="nav-services">Services</Link>
            <a href="#categories" className="hover:text-blue-600" data-testid="nav-categories">Categories</a>
            <button onClick={() => nav("/deals")} className="hover:text-blue-600" data-testid="nav-deals">Deals</button>
            <a href="#sellers" className="hover:text-blue-600" data-testid="nav-sellers">Sellers</a>
            <Link to="/help" className="hover:text-blue-600" data-testid="nav-help">Help</Link>
          </nav>
        </div>
        {banner?.enabled && (banner?.image || banner?.text) && (
          <button onClick={() => { trackHome("banner", "click"); banner.link && (banner.link.startsWith("http") ? window.open(banner.link, "_blank") : nav(banner.link)); }}
            data-testid="home-banner" className={banner?.image ? "block w-full" : "block w-full bg-gradient-to-r from-blue-600 via-violet-600 to-fuchsia-600 px-4 py-2 text-center text-sm font-semibold text-white"}>
            {banner?.image ? <img src={banner.image} alt={banner.text || "promotion"} className="block aspect-[12/1] w-full object-cover" /> : banner.text}
          </button>
        )}
      </header>

      {/* Hero carousel (Super Admin controlled) with polished marketplace fallback */}
      <HeroCarousel slides={heroSlides} hero={hero} goShop={goShop} nav={nav} />

      <main className="mx-auto max-w-[1500px] px-4 lg:px-8">
        <HomeTravelServices nav={nav} />
        <section className="py-8" data-testid="section-partner-featured">
          <div className="mb-1 flex items-end justify-between">
            <h2 className="text-2xl font-bold">Featured products</h2>
            <button onClick={() => goShop("")} className="inline-flex items-center gap-1 text-sm font-semibold text-blue-600" data-testid="partner-featured-shopall">Shop all <ChevronRight className="h-4 w-4" /></button>
          </div>
          <p className="mb-4 text-sm text-slate-500">Handpicked deals from our partner stores — biggest discounts first.</p>
          <p className="mb-4 text-[11px] text-slate-400">Partner product prices &amp; stock are indicative — the final price and availability are confirmed on the store's site.</p>
          <TopDeals />
          <CategoryRows />
        </section>
        {sections.map(renderSection)}

        {/* Sell on Suffle */}
        <section className="my-10 overflow-hidden rounded-3xl bg-slate-900 p-8 text-white sm:p-10" data-testid="section-sell">
          <div className="flex flex-wrap items-center justify-between gap-6">
            <div className="max-w-xl"><h2 className="text-2xl font-bold sm:text-3xl">Sell on Suffle</h2><p className="mt-2 text-white/70">Reach customers through an AI-powered marketplace built for discovery, trust and smarter shopping.</p></div>
            <Link to="/register" data-testid="become-seller" className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-slate-900">Become a Seller <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-slate-50" data-testid="market-footer">
        <div className="mx-auto grid max-w-[1500px] gap-6 px-4 py-10 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8 lg:px-8">
          <div><Logo variant="lg" /><p className="mt-3 text-sm font-semibold text-slate-700">AI-Powered Marketplace</p><p className="text-xs text-slate-500">Shop. Discover. Suffle.</p></div>

          <FooterCol title="Marketplace" testid="footer-marketplace">
            <li><button onClick={() => goShop("")} className="hover:text-slate-800">Shop</button></li>
            <li><Link to="/services" className="hover:text-slate-800">Services</Link></li>
            <li><a href="#categories" className="hover:text-slate-800">Categories</a></li>
            <li><button onClick={() => nav("/deals")} className="hover:text-slate-800">Deals</button></li>
            <li><Link to="/register" className="hover:text-slate-800">Sell on Suffle</Link></li>
          </FooterCol>

          <FooterCol title="Account & Support" testid="footer-account">
            <li><Link to="/login" className="hover:text-slate-800">Orders</Link></li>
            <li><Link to="/login" className="hover:text-slate-800">Wishlist</Link></li>
            <li><Link to="/login" className="hover:text-slate-800">Account</Link></li>
            <li><Link to="/help" className="hover:text-slate-800">Support</Link></li>
            <li><Link to="/legal/grievance-contact" className="hover:text-slate-800">Contact Us</Link></li>
          </FooterCol>

          <FooterLegal />
        </div>
        <div className="border-t border-slate-200 py-4 text-center text-xs text-slate-400">© {new Date().getFullYear()} Suffle Online · Naucera Travel Private Limited · Shop. Discover. Suffle.</div>
      </footer>
    </div>
  );
}

/* Footer nav column — collapsible on mobile (<details>), always open on desktop. */
function FooterCol({ title, testid, children }) {
  return (
    <details open className="group text-sm [&_summary::-webkit-details-marker]:hidden lg:[&>summary]:pointer-events-none" data-testid={testid}>
      <summary className="mb-2 flex cursor-pointer list-none items-center justify-between font-semibold text-slate-700 lg:cursor-default">
        {title}
        <ChevronDown className="h-4 w-4 text-slate-400 transition-transform group-open:rotate-180 lg:hidden" />
      </summary>
      <ul className="space-y-1 pb-2 text-slate-500 lg:pb-0">{children}</ul>
    </details>
  );
}

/* Legal column — dynamically shows only PUBLISHED policies from the centralized Legal system.
   Curated marketplace order; unpublished/missing slugs are automatically hidden (no 404s). */
const FOOTER_LEGAL = [
  ["Terms & Conditions", "terms-conditions"],
  ["Privacy Policy", "privacy-policy"],
  ["Returns & Refunds", "refund-cancellation"],
  ["Payment & Billing", "payment-information"],
];

function FooterLegal() {
  const [available, setAvailable] = useState(null);
  useEffect(() => {
    api.get("/legal").then((r) => setAvailable(new Set((r.data.pages || []).map((p) => p.slug)))).catch(() => setAvailable(new Set()));
  }, []);
  const items = FOOTER_LEGAL.filter(([, slug]) => !available || available.has(slug));
  return (
    <FooterCol title="Legal" testid="footer-legal">
      {items.map(([label, slug]) => (
        <li key={slug}><Link to={`/legal/${slug}`} className="hover:text-slate-800" data-testid={`footer-legal-${slug}`}>{label}</Link></li>
      ))}
      <li><Link to="/legal" className="font-medium text-slate-600 hover:text-slate-800" data-testid="footer-legal-all">All Policies →</Link></li>
    </FooterCol>
  );
}


/* Hero carousel — Super Admin controlled slides with a polished marketplace fallback. */
function HeroVisual({ hero }) {
  // Auto-rotating "screensaver" of hero images with manual scroll (arrows + dots).
  const custom = Array.from(new Set([hero?.image, ...(Array.isArray(hero?.images) ? hero.images : [])].filter(Boolean)));
  const imgs = custom.length
    ? custom
    : Array.from(new Set([HERO_IMG, CAT_IMG.electronics, CAT_IMG.womens, CAT_IMG.beauty, CAT_IMG.home].filter(Boolean)));
  const [i, setI] = _useStateHV(0);
  const [paused, setPaused] = _useStateHV(false);
  const n = imgs.length;
  _useEffectHV(() => {
    if (n <= 1 || paused) return;
    const ms = Math.max(1500, (Number(hero?.slideshow_speed) || 4.5) * 1000);
    const t = setInterval(() => setI((p) => (p + 1) % n), ms);
    return () => clearInterval(t);
  }, [n, paused, hero?.slideshow_speed]);
  const go = (d) => setI((p) => (p + d + n) % n);
  return (
    <div className="group relative aspect-[4/3] w-full overflow-hidden rounded-3xl shadow-xl ring-1 ring-white/60"
      onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} data-testid="hero-slideshow">
      {imgs.map((src, idx) => (
        <img key={idx} src={src} alt="Shop on Suffle" data-testid={`hero-slide-${idx}`}
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${idx === i ? "opacity-100" : "opacity-0"}`} />
      ))}
      {n > 1 && (
        <>
          <button onClick={() => go(-1)} aria-label="Previous" data-testid="hero-prev"
            className="absolute left-2 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/85 px-2.5 py-1.5 text-lg font-bold text-slate-700 opacity-0 shadow transition group-hover:opacity-100 hover:bg-white">‹</button>
          <button onClick={() => go(1)} aria-label="Next" data-testid="hero-next"
            className="absolute right-2 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/85 px-2.5 py-1.5 text-lg font-bold text-slate-700 opacity-0 shadow transition group-hover:opacity-100 hover:bg-white">›</button>
          <div className="absolute bottom-3 left-1/2 z-10 flex -translate-x-1/2 gap-1.5">
            {imgs.map((_, idx) => (
              <button key={idx} onClick={() => setI(idx)} aria-label={`Go to image ${idx + 1}`} data-testid={`hero-dot-${idx}`}
                className={`h-1.5 rounded-full transition-all ${idx === i ? "w-5 bg-white" : "w-1.5 bg-white/60 hover:bg-white/90"}`} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function HeroCarousel({ slides, hero, goShop, nav }) {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const n = slides?.length || 0;
  useEffect(() => {
    if (n <= 1 || paused) return;
    const t = setInterval(() => setI((x) => (x + 1) % n), 5000);
    return () => clearInterval(t);
  }, [n, paused]);
  useEffect(() => { if (i >= n && n > 0) setI(0); }, [n, i]);

  const go = (s) => {
    if (!s?.link) return goShop("");
    trackHome("hero_slide", "click");
    s.link.startsWith("http") ? window.open(s.link, "_blank") : nav(s.link);
  };

  if (n > 0) {
    return (
      <section className="relative mx-auto max-w-[1500px] px-4 pt-4 lg:px-8" data-testid="hero-carousel"
        onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
        <div className="relative overflow-hidden rounded-3xl bg-slate-100 shadow-sm ring-1 ring-slate-200">
          {slides.map((s, idx) => (
            <button key={idx} onClick={() => go(s)} data-testid={`hero-slide-${idx}`}
              className={`block w-full text-left transition-opacity duration-700 ${idx === i ? "opacity-100" : "pointer-events-none absolute inset-0 opacity-0"}`}>
              <picture>
                {s.mobile_image && <source media="(max-width: 640px)" srcSet={s.mobile_image} />}
                <img src={s.image || s.mobile_image} alt={s.title || "Promotion"} loading={idx === 0 ? "eager" : "lazy"}
                  className="aspect-[16/9] w-full object-cover sm:aspect-[16/6]" />
              </picture>
              {(s.title || s.subtitle || s.cta_label) && (
                <div className="absolute inset-0 flex flex-col justify-center gap-2 bg-gradient-to-r from-black/55 via-black/25 to-transparent p-6 sm:p-10 lg:p-14">
                  {s.title && <h2 className="max-w-lg text-2xl font-bold text-white drop-shadow sm:text-4xl">{s.title}</h2>}
                  {s.subtitle && <p className="max-w-md text-sm text-white/90 sm:text-base">{s.subtitle}</p>}
                  {s.cta_label && <span className="mt-1 inline-flex w-fit items-center gap-1.5 rounded-full bg-white px-5 py-2 text-sm font-semibold text-slate-900">{s.cta_label} <ArrowRight className="h-4 w-4" /></span>}
                </div>
              )}
            </button>
          ))}
          {n > 1 && (<>
            <button onClick={() => setI((x) => (x - 1 + n) % n)} data-testid="hero-prev" aria-label="Previous" className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-white/85 p-2 text-slate-700 shadow hover:bg-white"><ChevronLeft className="h-5 w-5" /></button>
            <button onClick={() => setI((x) => (x + 1) % n)} data-testid="hero-next" aria-label="Next" className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-white/85 p-2 text-slate-700 shadow hover:bg-white"><ChevronRight className="h-5 w-5" /></button>
            <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-1.5" data-testid="hero-dots">
              {slides.map((_, d) => <button key={d} onClick={() => setI(d)} data-testid={`hero-dot-${d}`} aria-label={`Go to slide ${d + 1}`} className={`h-2 rounded-full transition-all ${d === i ? "w-6 bg-white" : "w-2 bg-white/60"}`} />)}
            </div>
          </>)}
        </div>
      </section>
    );
  }

  // Fallback — clean, marketplace-first hero with real imagery (no empty AI illustration).
  return (
    <section className="relative overflow-hidden" style={{ background: "linear-gradient(120deg,#eff6ff 0%,#f5f3ff 55%,#fdf2f8 100%)" }} data-testid="hero-fallback">
      <div className="mx-auto grid max-w-[1500px] items-center gap-8 px-4 py-10 lg:grid-cols-[1.05fr_1fr] lg:px-8 lg:py-14">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-1.5 text-xs font-semibold text-slate-600"><Sparkles className="h-3.5 w-3.5 text-fuchsia-500" /> {hero.badge || "AI-Powered Marketplace"}</span>
          <h1 className="mt-4 text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl lg:text-6xl">Shop. Discover. <span className="bg-gradient-to-r from-blue-600 via-violet-600 to-fuchsia-600 bg-clip-text text-transparent">Suffle.</span></h1>
          <p className="mt-4 max-w-lg text-base text-slate-600 sm:text-lg">{hero.subtitle || "Shop from verified sellers, discover products near you, and let Suffle AI help you find exactly what you need."}</p>
          <div className="mt-6 flex flex-wrap gap-2">
            {CHIPS.map((c) => (
              <button key={c.label} onClick={() => goShop(c.q)} data-testid={`chip-${c.q.slice(0, 6)}`} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 hover:border-blue-400 hover:text-blue-600"><c.icon className="h-3.5 w-3.5" /> {c.label}</button>
            ))}
          </div>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <button onClick={() => goShop("")} data-testid="hero-shop-now" className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-600 to-fuchsia-600 px-5 py-2.5 text-sm font-semibold text-white hover:opacity-90">Start shopping <ArrowRight className="h-4 w-4" /></button>
            <Link to="/services" data-testid="hero-book-service" className="inline-flex items-center gap-2 rounded-full border border-fuchsia-200 bg-white px-5 py-2.5 text-sm font-semibold text-fuchsia-700 hover:bg-fuchsia-50"><Sparkles className="h-4 w-4" /> Book a Service</Link>
          </div>
          <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-600">
            {TRUST.map((t) => <span key={t.t} className="flex items-center gap-1.5"><t.icon className="h-4 w-4 text-emerald-500" /> {t.t}</span>)}
          </div>
        </div>
        <div className="relative mt-8 lg:mt-0" data-testid="hero-fallback-visual">
          <HeroVisual hero={hero} />
          <div className="absolute -bottom-4 -left-4 z-10 rounded-2xl bg-white/95 px-4 py-3 shadow-lg ring-1 ring-slate-100">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-600">Verified Sellers</p>
            <p className="text-base font-bold text-slate-900">Shop with confidence</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function ProductMini({ p, nav, rail }) {
  const d = p.mrp && p.price && p.mrp > p.price ? Math.round((1 - p.price / p.mrp) * 100) : 0;
  const img = p.thumb || (p.images || [])[0] || p.image;
  const go = () => { if (rail) trackHome(rail, "click", p.product_id); nav(`/product/${p.slug || p.product_id}`); };
  return (
    <div onClick={go} data-testid={`rail-prod-${p.product_id}`} className="w-40 shrink-0 cursor-pointer overflow-hidden rounded-2xl border border-slate-200 bg-white transition hover:shadow-md">
      <div className="aspect-square bg-slate-100">{img ? <img src={img} alt={p.name} loading="lazy" className="h-full w-full object-contain" /> : <div className="flex h-full items-center justify-center"><Package className="h-8 w-8 text-slate-300" /></div>}</div>
      <div className="p-3">
        <p className="line-clamp-2 text-sm font-medium">{p.name}</p>
        <div className="mt-1 flex items-baseline gap-1"><span className="font-bold">₹{Math.round(p.price || 0)}</span>{d > 0 && <span className="text-xs font-semibold text-emerald-600">{d}% OFF</span>}</div>
        {(p.rating_avg || p.rating) ? <p className="mt-1 flex items-center gap-1 text-xs text-amber-500"><Star className="h-3 w-3 fill-amber-400" /> {p.rating_avg || p.rating}{p.rating_count ? ` (${p.rating_count})` : ""}</p> : null}
      </div>
    </div>
  );
}

function Rail({ id, rail, title, subtitle, items, nav, onView }) {
  const impressed = useRef(false);
  useEffect(() => { if (items?.length && rail && !impressed.current) { impressed.current = true; trackHome(rail, "impression"); } }, [items, rail]);
  if (!items || items.length === 0) return null;
  return (
    <section className="py-8" data-testid={id}>
      <div className="mb-4 flex items-end justify-between"><div><h2 className="text-2xl font-bold">{title}</h2>{subtitle && <p className="text-sm text-slate-500">{subtitle}</p>}</div><button onClick={onView} className="inline-flex items-center gap-1 text-sm font-semibold text-blue-600">View All <ChevronRight className="h-4 w-4" /></button></div>
      <div className="flex gap-3 overflow-x-auto pb-2">{items.map((p, idx) => <ProductMini key={`${p.product_id}-${idx}`} p={p} nav={nav} rail={rail} />)}</div>
    </section>
  );
}
